import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AppNotification, NotificationListItem } from '../../../../core/models/notification-api.models';
import { AuthService } from '../../../../core/services/auth.service';
import { NotificationApiService } from '../../../../core/services/notification-api.service';
import { NotificationCounterService } from '../../../../core/services/notification-counter.service';
import { NotificationWebSocketService } from '../../../../core/services/notification-websocket.service';
import {
  formatNotificationDateTime,
  getNotificationIcon,
  getNotificationIconColor,
  groupNotificationsByDate,
  mergeLiveNotifications,
} from '../../../../core/utils/notification.utils';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
})
export class NotificationsComponent implements OnInit, OnDestroy {
  @ViewChild('listContainer') listContainer?: ElementRef<HTMLElement>;

  listItems: NotificationListItem[] = [];
  loading = true;
  loadingMore = false;
  errorMessage = '';
  hasMore = true;

  private currentPage = 1;
  private paginatedNotifications: AppNotification[] = [];
  private liveNotifications: AppNotification[] = [];
  private userId: string | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly authService: AuthService,
    private readonly notificationApi: NotificationApiService,
    private readonly counter: NotificationCounterService,
    private readonly websocket: NotificationWebSocketService,
    private readonly sanitizer: DomSanitizer,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.userId = this.authService.getUserId();
    if (!this.userId) {
      this.loading = false;
      this.errorMessage = 'Profil utilisateur indisponible.';
      return;
    }

    this.websocket.liveMessages$.pipe(takeUntil(this.destroy$)).subscribe((live) => {
      this.liveNotifications = live;
      this.rebuildList();
    });

    this.loadInitial();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  refresh(): void {
    this.currentPage = 1;
    this.paginatedNotifications = [];
    this.hasMore = true;
    this.loadInitial();
  }

  onScroll(): void {
    const element = this.listContainer?.nativeElement;
    if (!element || this.loading || this.loadingMore || !this.hasMore) {
      return;
    }

    const nearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 48;
    if (nearBottom) {
      this.loadMore();
    }
  }

  deleteNotification(notification: AppNotification): void {
    const confirmed = window.confirm('Supprimer la notification ?');
    if (!confirmed) {
      return;
    }

    this.notificationApi.delete(notification.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.paginatedNotifications = this.paginatedNotifications.filter((n) => n.id !== notification.id);
        this.websocket.removeLiveMessage(notification.id);
        this.counter.decrement();
        this.rebuildList();
      },
      error: () => {
        this.errorMessage = 'Impossible de supprimer la notification.';
      },
    });
  }

  renderContent(content: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(content);
  }

  iconFor(notification: AppNotification): string {
    return getNotificationIcon(notification);
  }

  iconColorFor(notification: AppNotification): string {
    return getNotificationIconColor(notification);
  }

  dateTimeFor(notification: AppNotification): string {
    return formatNotificationDateTime(notification.sendingDate);
  }

  trackByIndex(index: number, item: NotificationListItem): string {
    if (item.kind === 'date') {
      return `date-${item.dateLabel}`;
    }
    return item.notification?.id ?? `item-${index}`;
  }

  goBack(): void {
    this.router.navigate(['/accueil']);
  }

  private loadInitial(): void {
    if (!this.userId) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.notificationApi.fetchPage(this.userId, 1).pipe(takeUntil(this.destroy$)).subscribe({
      next: (page) => {
        this.paginatedNotifications = page.data;
        this.currentPage = 1;
        this.hasMore = page.currentpage < page.pages;
        this.markLoadedAsRead(page.data);
        this.rebuildList();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'Impossible de charger les notifications.';
      },
    });
  }

  private loadMore(): void {
    if (!this.userId || !this.hasMore) {
      return;
    }

    this.loadingMore = true;
    const nextPage = this.currentPage + 1;

    this.notificationApi.fetchPage(this.userId, nextPage).pipe(takeUntil(this.destroy$)).subscribe({
      next: (page) => {
        this.paginatedNotifications = [...this.paginatedNotifications, ...page.data];
        this.currentPage = nextPage;
        this.hasMore = page.currentpage < page.pages;
        this.markLoadedAsRead(page.data);
        this.rebuildList();
        this.loadingMore = false;
      },
      error: () => {
        this.loadingMore = false;
        this.errorMessage = 'Impossible de charger plus de notifications.';
      },
    });
  }

  private markLoadedAsRead(notifications: AppNotification[]): void {
    const ids = notifications.map((item) => item.id).filter(Boolean);
    if (!ids.length) {
      return;
    }

    this.notificationApi.markAsRead(ids).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.counter.decrement(ids.length),
      error: () => undefined,
    });
  }

  private rebuildList(): void {
    const merged = mergeLiveNotifications(this.paginatedNotifications, this.liveNotifications);
    this.listItems = groupNotificationsByDate(merged);
  }
}
